/**
 * Fiscal year parsing utilities shared by the FiscalYearInput component and the
 * Commenda OS integration hook.
 *
 * A fiscal year value can arrive in two shapes:
 *  - a "MM-DD" string (e.g. "12-31")
 *  - a DSL object: { type: "FIXED_DATE", data: { FIXED_DATE: { month: "DEC", day: 31 } } }
 *
 * NOTE: the two exported functions intentionally differ in how they treat an
 * unknown month name. `parseFiscalYearValue` (UI display) falls back to an empty
 * month string, while `normalizeFiscalYearValue` (submit) returns null. This
 * mirrors the historical behavior of the two call sites exactly — do not unify.
 */

// Month name (JAN..DEC) to zero-padded month number ("01".."12")
export const MONTH_NAME_TO_NUMBER: Record<string, string> = {
  JAN: '01',
  FEB: '02',
  MAR: '03',
  APR: '04',
  MAY: '05',
  JUN: '06',
  JUL: '07',
  AUG: '08',
  SEP: '09',
  OCT: '10',
  NOV: '11',
  DEC: '12',
};

interface FiscalYearDSLObject {
  type?: string;
  data?: { FIXED_DATE?: { month?: string; day?: number } };
}

/**
 * Parse a stored fiscal year value into zero-padded month/day strings for UI
 * display. Always returns an object; invalid/empty inputs yield empty strings.
 *
 * Behavior (preserved verbatim from FiscalYearInput):
 *  - empty/falsy value        → { month: '', day: '' }
 *  - "MM-DD" string           → padded { month, day }
 *  - non-matching string      → { month: '', day: '' }
 *  - DSL object               → { month: MONTH_NAME_TO_NUMBER[name] || '', day: padded }
 *  - anything else            → { month: '', day: '' }
 */
export function parseFiscalYearValue(value: unknown): {
  month: string;
  day: string;
} {
  if (!value) return { month: '', day: '' };

  if (typeof value === 'string') {
    const match = /^(\d{1,2})-(\d{1,2})$/.exec(value);
    if (!match) return { month: '', day: '' };
    return {
      month: match[1].padStart(2, '0'),
      day: match[2].padStart(2, '0'),
    };
  }

  if (typeof value === 'object' && value !== null) {
    const fixedDateData = (value as FiscalYearDSLObject).data?.FIXED_DATE;
    if (fixedDateData?.month && fixedDateData?.day) {
      const monthNum = MONTH_NAME_TO_NUMBER[fixedDateData.month] || '';
      const dayNum = fixedDateData.day.toString().padStart(2, '0');
      return { month: monthNum, day: dayNum };
    }
  }

  return { month: '', day: '' };
}

/**
 * Normalize a fiscal year value to a "MM-DD" string for submission, or null if
 * it cannot be resolved.
 *
 * Behavior (preserved verbatim from useCommendaOSIntegration):
 *  - "MM-DD" string           → padded "MM-DD"
 *  - non-matching string      → null
 *  - DSL object               → "MM-DD" only when the month name is known, else null
 *  - anything else            → null
 */
export function normalizeFiscalYearValue(value: unknown): string | null {
  // Already in correct format
  if (typeof value === 'string') {
    const match = /^(\d{1,2})-(\d{1,2})$/.exec(value);
    if (match) {
      return `${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`;
    }
    return null;
  }

  // Handle DSL object format
  if (typeof value === 'object' && value !== null) {
    const fixedDateData = (value as FiscalYearDSLObject).data?.FIXED_DATE;
    if (fixedDateData?.month && fixedDateData?.day) {
      const monthNum = MONTH_NAME_TO_NUMBER[fixedDateData.month];
      if (monthNum) {
        const dayNum = fixedDateData.day.toString().padStart(2, '0');
        return `${monthNum}-${dayNum}`;
      }
    }
  }

  return null;
}
